import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, CheckCircle, Music, GraduationCap, Calendar, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";

const Newsletter = () => {
  useEffect(() => {
    // Load Brevo form script
    const script = document.createElement("script");
    script.src = "https://sibforms.com/forms/end-form/build/main.js";
    script.defer = true;
    document.body.appendChild(script);

    // Set global variables for Brevo form
    (window as any).REQUIRED_CODE_ERROR_MESSAGE = 'Wähle bitte einen Ländervorwahl aus.';
    (window as any).LOCALE = 'de';
    (window as any).EMAIL_INVALID_MESSAGE = (window as any).SMS_INVALID_MESSAGE = "Die eingegebenen Informationen sind nicht gültig. Bitte überprüfe das Feldformat und versuche es erneut.";
    (window as any).REQUIRED_ERROR_MESSAGE = "Dieses Feld darf nicht leer sein. ";
    (window as any).GENERIC_INVALID_MESSAGE = "Die eingegebenen Informationen sind nicht gültig. Bitte überprüfe das Feldformat und versuche es erneut.";
    (window as any).translation = {
      common: {
        selectedList: '{quantity} Liste ausgewählt',
        selectedLists: '{quantity} Listen ausgewählt',
        selectedOption: '{quantity} ausgewählt',
        selectedOptions: '{quantity} ausgewählt',
      }
    };
    (window as any).AUTOHIDE = Boolean(0);

    // Meta Pixel Subscribe-Event: feuert wenn Brevo-Erfolgsmeldung sichtbar wird.
    // Nur einmal pro Session, um Doppel-Events bei Re-Renders zu verhindern.
    const SUBSCRIBE_FIRED_KEY = "meta_pixel_subscribe_fired";
    const setupPixelObserver = () => {
      const successEl = document.getElementById("success-message");
      if (!successEl) return;

      const observer = new MutationObserver(() => {
        const isVisible = successEl.style.display !== "none" && successEl.style.display !== "";
        const alreadyFired = sessionStorage.getItem(SUBSCRIBE_FIRED_KEY) === "1";
        if (isVisible && !alreadyFired && (window as any).fbq) {
          (window as any).fbq("track", "Subscribe", {
            content_name: "Newsletter Anmeldung",
            content_category: "newsletter",
          });
          sessionStorage.setItem(SUBSCRIBE_FIRED_KEY, "1");
          observer.disconnect();
        }
      });

      observer.observe(successEl, { attributes: true, attributeFilter: ["style"] });
      return observer;
    };

    // Delay slightly to ensure Brevo DOM is ready
    const timer = setTimeout(() => {
      const obs = setupPixelObserver();
      cleanupObserver = obs;
    }, 1000);

    let cleanupObserver: MutationObserver | undefined;

    return () => {
      document.body.removeChild(script);
      clearTimeout(timer);
      cleanupObserver?.disconnect();
    };
  }, []);

  const benefits = [
    {
      icon: Calendar,
      title: "Termine & Neuigkeiten",
      description: "Erfahre als Erste/r von neuen Webinar-Terminen und Ausbildungsangeboten"
    },
    {
      icon: Music,
      title: "Exklusive Inhalte",
      description: "Erhalte kostenlose Praxistipps, Lieder und Anleitungen für deinen Alltag"
    },
    {
      icon: GraduationCap,
      title: "Rabatte & Aktionen",
      description: "Sichere dir Newsletter-exklusive Rabatte auf Kurse und Ausbildungen"
    },
    {
      icon: Sparkles,
      title: "Inspiration & Ideen",
      description: "Lass dich von kreativen Impulsen für deine musikalische Arbeit inspirieren"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/20 to-accent/20">
      <SEO title={`Newsletter – Praxis-Tipps für Musikpädagogik | MusiKonzept`} description={`Melde dich für den MusiKonzept-Newsletter an und erhalte regelmäßig kostenlose Praxis-Tipps, Lieder und Aktionsangebote.`} path="/newsletter" />
      <Header />
      
      {/* Brevo Styles */}
      <style dangerouslySetInnerHTML={{__html: `
        @font-face {
          font-display: block;
          font-family: Roboto;
          src: url(https://assets.brevo.com/font/Roboto/Latin/normal/normal/7529907e9eaf8ebb5220c5f9850e3811.woff2) format("woff2"), url(https://assets.brevo.com/font/Roboto/Latin/normal/normal/25c678feafdc175a70922a116c9be3e7.woff) format("woff")
        }
        @font-face {
          font-display: fallback;
          font-family: Roboto;
          font-weight: 600;
          src: url(https://assets.brevo.com/font/Roboto/Latin/medium/normal/6e9caeeafb1f3491be3e32744bc30440.woff2) format("woff2"), url(https://assets.brevo.com/font/Roboto/Latin/medium/normal/71501f0d8d5aa95960f6475d5487d4c2.woff) format("woff")
        }
        @font-face {
          font-display: fallback;
          font-family: Roboto;
          font-weight: 700;
          src: url(https://assets.brevo.com/font/Roboto/Latin/bold/normal/3ef7cf158f310cf752d5ad08cd0e7e60.woff2) format("woff2"), url(https://assets.brevo.com/font/Roboto/Latin/bold/normal/ece3a1d82f18b60bcce0211725c476aa.woff) format("woff")
        }
        #sib-container input:-ms-input-placeholder {
          text-align: left;
          font-family: Helvetica, sans-serif;
          color: #c0ccda;
        }
        #sib-container input::placeholder {
          text-align: left;
          font-family: Helvetica, sans-serif;
          color: #c0ccda;
        }
        #sib-container textarea::placeholder {
          text-align: left;
          font-family: Helvetica, sans-serif;
          color: #c0ccda;
        }
      `}} />
      <link rel="stylesheet" href="https://sibforms.com/forms/end-form/build/sib-styles.css" />

      <main className="pt-20">
        {/* Hero Section */}
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center animate-bounce-slow">
                <Mail className="w-10 h-10 text-primary" />
              </div>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
              Bleib auf dem Laufenden
            </h1>
            <p className="text-xl text-muted-foreground mb-4 max-w-2xl mx-auto">
              Melde dich für unseren Newsletter an und verpasse keine Neuigkeiten mehr rund um musikalische Frühförderung, Weiterbildungen und kreative Praxisideen.
            </p>
            <p className="text-lg text-muted-foreground/80">
              Kostenlos, unverbindlich und jederzeit abmeldbar.
            </p>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="container mx-auto px-6 pb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Das erwartet dich</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-card/50 backdrop-blur-sm rounded-2xl p-6 border border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-lg animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <benefit.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Newsletter Form Section */}
        <div className="container mx-auto px-6 pb-20">
          <div className="max-w-2xl mx-auto">
            <div className="bg-card/70 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-border/50 shadow-2xl animate-fade-in">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-4">Jetzt anmelden</h2>
                <p className="text-muted-foreground">
                  Trage deine Daten ein und erhalte regelmäßig wertvolle Impulse direkt in dein Postfach.
                </p>
              </div>

              {/* Brevo Form */}
              <div dangerouslySetInnerHTML={{__html: `
                <div class="sib-form" style="text-align: center; background-color: transparent;">
                  <div id="sib-form-container" class="sib-form-container">
                    <div id="error-message" class="sib-form-message-panel" style="font-size:16px; text-align:left; font-family:Helvetica, sans-serif; color:#661d1d; background-color:#ffeded; border-radius:12px; border-color:#ff4949; display:none; padding: 16px; margin-bottom: 16px;">
                      <div class="sib-form-message-panel__text sib-form-message-panel__text--center">
                        <svg viewBox="0 0 512 512" class="sib-icon sib-notification__icon" style="width: 20px; height: 20px; display: inline-block; vertical-align: middle; margin-right: 8px;">
                          <path d="M256 40c118.621 0 216 96.075 216 216 0 119.291-96.61 216-216 216-119.244 0-216-96.562-216-216 0-119.203 96.602-216 216-216m0-32C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm-11.49 120h22.979c6.823 0 12.274 5.682 11.99 12.5l-7 168c-.268 6.428-5.556 11.5-11.99 11.5h-8.979c-6.433 0-11.722-5.073-11.99-11.5l-7-168c-.283-6.818 5.167-12.5 11.99-12.5zM256 340c-15.464 0-28 12.536-28 28s12.536 28 28 28 28-12.536 28-28-12.536-28-28-28z" />
                        </svg>
                        <span class="sib-form-message-panel__inner-text">
                          Deine Anmeldung konnte nicht gespeichert werden. Bitte versuche es erneut.
                        </span>
                      </div>
                    </div>
                    <div id="success-message" class="sib-form-message-panel" style="font-size:16px; text-align:left; font-family:Helvetica, sans-serif; color:#085229; background-color:#e7faf0; border-radius:12px; border-color:#13ce66; display:none; padding: 16px; margin-bottom: 16px;">
                      <div class="sib-form-message-panel__text sib-form-message-panel__text--center">
                        <svg viewBox="0 0 512 512" class="sib-icon sib-notification__icon" style="width: 20px; height: 20px; display: inline-block; vertical-align: middle; margin-right: 8px;">
                          <path d="M256 8C119.033 8 8 119.033 8 256s111.033 248 248 248 248-111.033 248-248S392.967 8 256 8zm0 464c-118.664 0-216-96.055-216-216 0-118.663 96.055-216 216-216 118.664 0 216 96.055 216 216 0 118.663-96.055 216-216 216zm141.63-274.961L217.15 376.071c-4.705 4.667-12.303 4.637-16.97-.068l-85.878-86.572c-4.667-4.705-4.637-12.303.068-16.97l8.52-8.451c4.705-4.667 12.303-4.637 16.97.068l68.976 69.533 163.441-162.13c4.705-4.667 12.303-4.637 16.97.068l8.451 8.52c4.668 4.705 4.637 12.303-.068 16.97z" />
                        </svg>
                        <span class="sib-form-message-panel__inner-text">
                          Deine Anmeldung war erfolgreich! Wir haben dir eine E-Mail geschickt.
                        </span>
                      </div>
                    </div>
                    <div id="sib-container" class="sib-container--large sib-container--vertical" style="text-align:center; background-color:transparent; max-width:540px; border-radius:25px; border-width:0px; margin: 0 auto;">
                      <form id="sib-form" method="POST" action="https://dfeda3f7.sibforms.com/serve/MUIFACEQB1BNzdwLMU_AGhHo1hqCUprVrnKrF5b64pNb5HY08pjh9CD_14BW2Q1EqSMDSaWWGAsX8xECBy1RIVODMKK6HC8mxP2cImX3J0ATZdR_Hk8cqqe2fuqahz5fQW9Fz-MwoybLg7b2chZ_6m-B9f-4rxlG0OYkGPLi-5Gzd5pRbyk68Ihe2A6v7hKsVk8ewzGgTmt4Euuc" data-type="subscription">
                        <div style="padding: 12px 0;">
                          <div class="sib-input sib-form-block">
                            <div class="form__entry entry_block">
                              <div class="form__label-row">
                                <div class="entry__field">
                                  <input class="input" style="width: 100%; padding: 14px 16px; border-radius: 12px; border: 1px solid #e2e8f0; font-size: 16px; background-color: white;" maxlength="200" type="text" id="VORNAME" name="VORNAME" autocomplete="off" placeholder="Vorname" data-required="true" required />
                                </div>
                              </div>
                              <label class="entry__error entry__error--primary" style="font-size:14px; text-align:left; font-family:Helvetica, sans-serif; color:#661d1d; background-color:#ffeded; border-radius:8px; padding: 8px; margin-top: 8px; display: none;"></label>
                            </div>
                          </div>
                        </div>
                        <div style="padding: 12px 0;">
                          <div class="sib-input sib-form-block">
                            <div class="form__entry entry_block">
                              <div class="form__label-row">
                                <div class="entry__field">
                                  <input class="input" style="width: 100%; padding: 14px 16px; border-radius: 12px; border: 1px solid #e2e8f0; font-size: 16px; background-color: white;" maxlength="200" type="text" id="NACHNAME" name="NACHNAME" autocomplete="off" placeholder="Nachname" data-required="true" required />
                                </div>
                              </div>
                              <label class="entry__error entry__error--primary" style="font-size:14px; text-align:left; font-family:Helvetica, sans-serif; color:#661d1d; background-color:#ffeded; border-radius:8px; padding: 8px; margin-top: 8px; display: none;"></label>
                            </div>
                          </div>
                        </div>
                        <div style="padding: 12px 0;">
                          <div class="sib-input sib-form-block">
                            <div class="form__entry entry_block">
                              <div class="form__label-row">
                                <div class="entry__field">
                                  <input class="input" style="width: 100%; padding: 14px 16px; border-radius: 12px; border: 1px solid #e2e8f0; font-size: 16px; background-color: white;" type="text" id="EMAIL" name="EMAIL" autocomplete="off" placeholder="E-Mail" data-required="true" required />
                                </div>
                              </div>
                              <label class="entry__error entry__error--primary" style="font-size:14px; text-align:left; font-family:Helvetica, sans-serif; color:#661d1d; background-color:#ffeded; border-radius:8px; padding: 8px; margin-top: 8px; display: none;"></label>
                            </div>
                          </div>
                        </div>
                        <div style="padding: 24px 0 12px 0;">
                          <div class="sib-form-block" style="text-align: center">
                            <button class="sib-form-block__button sib-form-block__button-with-loader" style="font-size:16px; text-align:center; font-weight:700; font-family:Helvetica, sans-serif; color:#FFFFFF; background-color:#49bdc2; border-radius:12px; border-width:0px; padding: 16px 48px; width: 100%; cursor: pointer; transition: all 0.3s ease;" form="sib-form" type="submit" onmouseover="this.style.backgroundColor='#3ba9ae'" onmouseout="this.style.backgroundColor='#49bdc2'">
                              <svg class="icon clickable__icon progress-indicator__icon sib-hide-loader-icon" viewBox="0 0 512 512" style="display: none;">
                                <path d="M460.116 373.846l-20.823-12.022c-5.541-3.199-7.54-10.159-4.663-15.874 30.137-59.886 28.343-131.652-5.386-189.946-33.641-58.394-94.896-95.833-161.827-99.676C261.028 55.961 256 50.751 256 44.352V20.309c0-6.904 5.808-12.337 12.703-11.982 83.556 4.306 160.163 50.864 202.11 123.677 42.063 72.696 44.079 162.316 6.031 236.832-3.14 6.148-10.75 8.461-16.728 5.01z" />
                              </svg>
                              Ja, bitte schickt mir Infos
                            </button>
                          </div>
                        </div>
                        <input type="text" name="email_address_check" value="" class="input--hidden" style="display: none;">
                        <input type="hidden" name="locale" value="de">
                      </form>
                    </div>
                  </div>
                </div>
              `}} />

              <div className="mt-8 pt-6 border-t border-border/50">
                <div className="flex items-start gap-3 text-sm text-muted-foreground">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <p>
                    Mit deiner Anmeldung erklärst du dich mit unserer{" "}
                    <a href="/datenschutz" className="text-primary hover:underline">
                      Datenschutzerklärung
                    </a>{" "}
                    einverstanden. Du kannst dich jederzeit wieder abmelden.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Newsletter;
